import { useState, useEffect } from 'react'
import { 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  CogIcon,
  ArrowPathIcon,
  UsersIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import { useToast } from '../ToastContainer'

interface Role {
  id: string
  name: string
  description?: string
  color: string
  createdAt: string
  updatedAt: string
  rolePermissions: Array<{
    permission: string
  }>
  _count: {
    projectMembers: number
    groupRoles: number
  }
}

interface Permission {
  value: string
  label: string
  category: string
}

interface CreateRoleForm {
  name: string
  description: string
  color: string
  permissions: string[]
}

interface RoleManagementState {
  roles: Role[]
  availablePermissions: Permission[]
  loading: boolean
  error: string | null
  showCreateModal: boolean
  showEditModal: boolean
  showDeleteModal: boolean
  showPermissionModal: boolean
  editingRole: Role | null
  deletingRoleId: string | null
  activeTab: 'roles' | 'permissions'
  initializingPermissions: boolean
}

const RoleManagement = () => {
  const { showSuccess, showError } = useToast()
  
  const [state, setState] = useState<RoleManagementState>({
    roles: [],
    availablePermissions: [],
    loading: true,
    error: null,
    showCreateModal: false,
    showEditModal: false,
    showDeleteModal: false,
    showPermissionModal: false,
    editingRole: null,
    deletingRoleId: null,
    activeTab: 'roles',
    initializingPermissions: false
  })

  const [createForm, setCreateForm] = useState<CreateRoleForm>({
    name: '',
    description: '',
    color: '#6b7280',
    permissions: []
  })

  useEffect(() => {
    Promise.all([
      fetchRoles(),
      fetchAvailablePermissions()
    ])
  }, [])

  const fetchRoles = async () => {
    try {
      const response = await fetch('/api/roles', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        throw new Error('獲取角色失敗')
      }

      const data = await response.json()
      setState(prev => ({ ...prev, roles: data.data.roles || [] }))
    } catch (error: any) {
      setState(prev => ({ ...prev, error: error.message }))
    }
  }

  const fetchAvailablePermissions = async () => {
    try {
      const response = await fetch('/api/roles/permissions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        throw new Error('獲取可用權限失敗')
      }

      const data = await response.json()
      setState(prev => ({ 
        ...prev, 
        availablePermissions: data.data.permissions || [],
        loading: false
      }))
    } catch (error: any) {
      setState(prev => ({ 
        ...prev, 
        error: error.message,
        loading: false
      }))
    }
  }

  const handleCreateRole = async () => {
    try {
      const response = await fetch('/api/roles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(createForm)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '建立角色失敗')
      }

      showSuccess('角色建立成功')
      setCreateForm({ name: '', description: '', color: '#6b7280', permissions: [] })
      setState(prev => ({ ...prev, showCreateModal: false }))
      await fetchRoles()
    } catch (error: any) {
      showError('操作失敗', error.message)
    }
  }

  const handleUpdateRole = async () => {
    if (!state.editingRole) return

    try {
      // 更新基本資訊
      const basicInfoResponse = await fetch(`/api/roles/${state.editingRole.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          name: createForm.name,
          description: createForm.description,
          color: createForm.color
        })
      })

      if (!basicInfoResponse.ok) {
        const errorData = await basicInfoResponse.json()
        throw new Error(errorData.message || '更新角色基本資訊失敗')
      }

      // 更新權限
      const permissionsResponse = await fetch(`/api/roles/${state.editingRole.id}/permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          permissions: createForm.permissions
        })
      })

      if (!permissionsResponse.ok) {
        const errorData = await permissionsResponse.json()
        throw new Error(errorData.message || '更新角色權限失敗')
      }

      showSuccess('角色更新成功')
      setState(prev => ({ ...prev, showEditModal: false, editingRole: null }))
      await fetchRoles()
    } catch (error: any) {
      showError('操作失敗', error.message)
    }
  }

  const handleDeleteRole = async () => {
    if (!state.deletingRoleId) return

    try {
      const response = await fetch(`/api/roles/${state.deletingRoleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '刪除角色失敗')
      }

      showSuccess('角色刪除成功')
      setState(prev => ({ ...prev, showDeleteModal: false, deletingRoleId: null }))
      await fetchRoles()
    } catch (error: any) {
      showError('操作失敗', error.message)
    }
  }

  const openCreateModal = () => {
    setCreateForm({ name: '', description: '', color: '#6b7280', permissions: [] })
    setState(prev => ({ ...prev, showCreateModal: true }))
  }

  const openEditModal = (role: Role) => {
    setCreateForm({
      name: role.name,
      description: role.description || '',
      color: role.color,
      permissions: (role.rolePermissions || []).map(rp => rp.permission)
    })
    setState(prev => ({ ...prev, showEditModal: true, editingRole: role }))
  }

  const resolvePermissionLabel = (permission: string): string => {
    const found = state.availablePermissions.find(p => p.value === permission)
    if (found) return found.label
    // 簡化權限標籤顯示
    return permission.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
  }

  const getPermissionsByCategory = () => {
    const categorized: Record<string, Permission[]> = {}
    state.availablePermissions.forEach(permission => {
      if (!categorized[permission.category]) {
        categorized[permission.category] = []
      }
      categorized[permission.category].push(permission)
    })
    return categorized
  }

  // ========== 權限初始化功能 ==========
  const initializeGlobalPermissions = async () => {
    setState(prev => ({ ...prev, initializingPermissions: true }))
    try {
      const response = await fetch('/api/permissions/initialize', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        throw new Error('初始化系統權限失敗')
      }

      showSuccess('系統權限初始化成功')
      await fetchRoles() // 重新載入角色以更新權限
    } catch (error: any) {
      showError('初始化失敗', error.message || '初始化系統權限失敗')
    } finally {
      setState(prev => ({ ...prev, initializingPermissions: false }))
    }
  }

  const resetRolePermissions = async (roleId: string) => {
    if (!window.confirm('確定要重置此角色的權限嗎？這將移除該角色的所有權限。')) {
      return
    }

    try {
      const response = await fetch(`/api/roles/${roleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          permissions: []
        })
      })

      if (!response.ok) {
        throw new Error('重置角色權限失敗')
      }

      showSuccess('角色權限已重置')
      await fetchRoles()
    } catch (error: any) {
      showError('操作失敗', error.message)
    }
  }

  if (state.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (state.error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">載入失敗</h3>
        <p className="mt-1 text-sm text-gray-500">{state.error}</p>
        <div className="mt-4">
          <button
            onClick={() => {
              setState(prev => ({ ...prev, loading: true, error: null }))
              Promise.all([fetchRoles(), fetchAvailablePermissions()])
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            重新載入
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <UserGroupIcon className="w-8 h-8 mr-3 text-blue-600" />
                角色與權限管理
              </h1>
              <p className="text-base text-gray-600 mt-1">建立和設定系統角色，並配置權限分配矩陣</p>
            </div>
            <div className="flex space-x-3">
              {state.activeTab === 'permissions' && (
                <button
                  onClick={initializeGlobalPermissions}
                  disabled={state.initializingPermissions}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center text-sm"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  {state.initializingPermissions ? '初始化中...' : '重設預設權限'}
                </button>
              )}
              {state.activeTab === 'roles' && (
                <button
                  onClick={openCreateModal}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center text-sm"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  建立角色
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="px-6">
          <nav className="flex space-x-8 -mb-px">
            <button
              onClick={() => setState(prev => ({ ...prev, activeTab: 'roles' }))}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                state.activeTab === 'roles'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <UserGroupIcon className="w-5 h-5 inline mr-2" />
              角色設定
            </button>
            <button
              onClick={() => setState(prev => ({ ...prev, activeTab: 'permissions' }))}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                state.activeTab === 'permissions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ShieldCheckIcon className="w-5 h-5 inline mr-2" />
              權限矩陣
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      {state.activeTab === 'roles' ? (
        /* Roles List */
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">角色設定</h2>
            <p className="text-sm text-gray-600">建立、編輯和管理系統角色的基本屬性</p>
          </div>
        
        <div className="divide-y divide-gray-200">
          {(state.roles || []).map((role) => (
            <div key={role.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: role.color }}
                    />
                    <h3 className="text-lg font-medium text-gray-900">{role.name}</h3>
                  </div>
                  {role.description && (
                    <p className="mt-1 text-sm text-gray-600">{role.description}</p>
                  )}
                  
                  <div className="mt-3 flex items-center space-x-6 text-sm text-gray-500">
                    <span className="flex items-center">
                      <CogIcon className="w-4 h-4 mr-1" />
                      {role.rolePermissions?.length || 0} 項權限
                    </span>
                    <span className="flex items-center">
                      <UsersIcon className="w-4 h-4 mr-1" />
                      {role._count.projectMembers + role._count.groupRoles} 使用中
                    </span>
                  </div>

                  {/* Permissions preview */}
                  {(role.rolePermissions || []).length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">角色權限:</h4>
                      <div className="flex flex-wrap gap-1">
                        {(role.rolePermissions || []).slice(0, 5).map((rp) => (
                          <span key={rp.permission} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {resolvePermissionLabel(rp.permission)}
                          </span>
                        ))}
                        {(role.rolePermissions || []).length > 5 && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            +{(role.rolePermissions || []).length - 5} 更多
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => openEditModal(role)}
                    className="text-gray-600 hover:text-gray-700 p-2"
                    title="編輯角色"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  
                  <button
                    onClick={() => setState(prev => ({ 
                      ...prev, 
                      showDeleteModal: true, 
                      deletingRoleId: role.id 
                    }))}
                    className="text-red-600 hover:text-red-700 p-2"
                    title="刪除角色"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        </div>
      ) : (
        /* Global Permissions Management */
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">權限矩陣</h2>
            <p className="text-sm text-gray-600">檢視和分析各角色的權限分配狀況</p>
          </div>
          
          <div className="p-6">
            <div className="space-y-6">
              {Object.entries(getPermissionsByCategory()).map(([categoryKey, permissions]) => (
                <div key={categoryKey} className="bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-100">
                    <h3 className="text-sm font-medium text-gray-900">{categoryKey}</h3>
                  </div>
                  <div className="p-4">
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr>
                            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">
                              權限
                            </th>
                            {(state.roles || []).map(role => (
                              <th key={role.id} className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider pb-2 px-3">
                                <div className="flex items-center justify-center space-x-2">
                                  <div 
                                    className="w-3 h-3 rounded-full" 
                                    style={{ backgroundColor: role.color }}
                                  />
                                  <span>{role.name}</span>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="space-y-2">
                          {permissions.map(permission => (
                            <tr key={permission.value} className="border-t border-gray-100">
                              <td className="py-2 text-sm text-gray-900 font-medium">
                                {resolvePermissionLabel(permission.value as any)}
                              </td>
                              {(state.roles || []).map(role => {
                                const hasPermission = (role.rolePermissions || []).some(
                                  rp => rp.permission === permission.value
                                )
                                const isViewIssue = permission.value === 'VIEW_ISSUE'
                                // VIEW_ISSUE 權限預設為 true
                                const displayPermission = isViewIssue || hasPermission
                                
                                return (
                                  <td key={role.id} className="py-2 text-center px-3">
                                    <div className="flex items-center justify-center space-x-2">
                                      <span className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                                        displayPermission
                                          ? isViewIssue 
                                            ? 'bg-blue-500 border-blue-500 text-white' // VIEW_ISSUE 用藍色表示必需權限
                                            : 'bg-green-500 border-green-500 text-white'
                                          : 'border-gray-300'
                                      }`}>
                                        {displayPermission && <span className="text-xs">✓</span>}
                                      </span>
                                      {!isViewIssue && (
                                        <button
                                          onClick={() => resetRolePermissions(role.id)}
                                          className="text-gray-400 hover:text-red-600 p-1"
                                          title={`重置 ${role.name} 權限`}
                                        >
                                          <ArrowPathIcon className="w-4 h-4" />
                                        </button>
                                      )}
                                      {isViewIssue && (
                                        <span className="text-xs text-blue-600 font-medium" title="必需權限">
                                          必需
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                )
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ))}
              
              {(state.roles || []).length === 0 && (
                <div className="text-center py-12">
                  <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">沒有角色</h3>
                  <p className="mt-1 text-sm text-gray-500">請先建立角色再配置權限</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Role Modal */}
      {(state.showCreateModal || state.showEditModal) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {state.showCreateModal ? '建立角色' : '編輯角色'}
                </h3>
                <button
                  onClick={() => setState(prev => ({ 
                    ...prev, 
                    showCreateModal: false, 
                    showEditModal: false, 
                    editingRole: null 
                  }))}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      角色名稱
                    </label>
                    <input
                      type="text"
                      value={createForm.name}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="輸入角色名稱"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      角色顏色
                    </label>
                    <input
                      type="color"
                      value={createForm.color}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, color: e.target.value }))}
                      className="w-full h-10 px-1 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    角色描述
                  </label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="輸入角色描述（可選）"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    角色權限
                  </label>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {Object.entries(getPermissionsByCategory()).map(([category, permissions]) => (
                      <div key={category} className="border border-gray-200 rounded-lg">
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                          <h4 className="text-sm font-medium text-gray-700">{category}</h4>
                        </div>
                        <div className="p-4 space-y-2">
                          {permissions.map((permission) => {
                            // VIEW_ISSUE 是必需權限，預設勾選且無法取消
                            const isViewIssue = permission.value === 'VIEW_ISSUE'
                            const isChecked = isViewIssue || createForm.permissions.includes(permission.value)
                            
                            return (
                              <label key={permission.value} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  disabled={isViewIssue}
                                  onChange={(e) => {
                                    if (isViewIssue) return // 防止修改 VIEW_ISSUE
                                    
                                    if (e.target.checked) {
                                      setCreateForm(prev => ({
                                        ...prev,
                                        permissions: [...prev.permissions, permission.value]
                                      }))
                                    } else {
                                      setCreateForm(prev => ({
                                        ...prev,
                                        permissions: prev.permissions.filter(p => p !== permission.value)
                                      }))
                                    }
                                  }}
                                  className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${
                                    isViewIssue ? 'opacity-50 cursor-not-allowed' : ''
                                  }`}
                                />
                                <span className={`ml-2 text-sm ${
                                  isViewIssue ? 'text-gray-500' : 'text-gray-900'
                                }`}>
                                  {permission.label}
                                  {isViewIssue && <span className="ml-1 text-xs text-blue-600">(必需權限)</span>}
                                </span>
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setState(prev => ({ 
                    ...prev, 
                    showCreateModal: false, 
                    showEditModal: false, 
                    editingRole: null 
                  }))}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  取消
                </button>
                <button
                  onClick={state.showCreateModal ? handleCreateRole : handleUpdateRole}
                  disabled={!createForm.name.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {state.showCreateModal ? '建立' : '更新'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {state.showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="text-lg font-medium text-gray-900 mt-4">確認刪除角色</h3>
              <p className="text-sm text-gray-500 mt-2">
                此操作無法復原，角色相關的權限分配將被移除。
              </p>
              <div className="flex justify-center space-x-3 mt-6">
                <button
                  onClick={() => setState(prev => ({ ...prev, showDeleteModal: false, deletingRoleId: null }))}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  取消
                </button>
                <button
                  onClick={handleDeleteRole}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  確認刪除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RoleManagement
